import React, { useEffect, useState } from 'react';

import { ApolloError } from 'apollo-client';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { ICartEntry, IServerOrder } from '@rbi-ctg/menu';
import Footer from 'components/footer';
import { SkeletonThemeType } from 'components/layout/skeleton-theme/types';
import { ModalBlockRegion } from 'components/modal-block-region';
import { useLoyaltyActiveCard } from 'hooks/loyalty';
import { useBlockedByRegion } from 'hooks/use-block-by-region';
import useMediaQuery, { MediaQuery } from 'hooks/use-media-query';
import useUserInSameRegion from 'hooks/use-user-in-same-region';
import { TimsRewardsPoints } from 'pages/account/tims-rewards/shared-onboarding-modal-components/tims-rewards-points';
import { UserDetails } from 'state/auth/hooks/types';
import { selectors, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { IncentiveEvaluationErrorCodes } from 'state/loyalty/hooks/types';
import { useOffersContext } from 'state/offers';
import { OrderStatus, useOrderContext } from 'state/order';
import { useCartEntriesDayParts } from 'state/order/hooks/use-cart-entries-day-parts';
import { useOrderTimedFire } from 'state/order/hooks/use-order-timed-fire';
import useRewardDiscount from 'state/order/hooks/use-reward-discount';
import { IPaymentMethod } from 'state/payment/types';
import { useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import { isCatering as isCateringOrder } from 'utils/service-mode';

import { AddOnsItemsContainer } from './addons-item-container/addons-item-container';
import CartPageContainer from './cart-page-container';
import { CartRewardDropdown } from './cart-reward-dropdown';
import Content from './content';
import { useCartHasUnavailableOffers } from './hooks/use-cart-has-unavailable-offers';
import { Payment } from './payment';
import { AddOnContainer, CartWrap, PaymentArea, StyledDetails } from './styled';
import { MobileCartUpsellLocation } from './types';
import { Upsell } from './upsell';
import YourCart from './your-cart';
import { CartErrorMessages } from './your-cart/cart-error-messages';
import { RewardsPointsContainer } from './your-cart/styled.shared';

const StyledUpsell = styled(Upsell)`
  grid-area: upsell;
`;

const StyledPayment = styled(Payment)`
  grid-area: payment;
`;

interface ICartContent {
  checkoutPaymentMethod?: IPaymentMethod | null;
  serverOrder: IServerOrder | null;
  cartEntries: ICartEntry[];
  user: UserDetails | null;
  orderStatus: OrderStatus | null;
  tryPricing: any;
  unavailableCartEntries: ICartEntry[];
  priceMutationError?: ApolloError;
  priceOrderSuccess: boolean;
  hasAddOns?: boolean;
}

const CartContent = ({
  serverOrder,
  cartEntries,
  user,
  orderStatus,
  tryPricing,
  unavailableCartEntries,
  priceMutationError,
  priceOrderSuccess,
  hasAddOns = false,
  checkoutPaymentMethod,
}: ICartContent) => {
  const orderCtx = useOrderContext();
  const { loyaltyDetails } = useLoyaltyActiveCard();
  const { isDelivery } = useServiceModeContext();
  const [areDeliveryDetailsValid, setAreDeliveryDetailsValid] = useState(
    isDelivery ? Boolean(orderCtx.orderPhoneNumber) : true
  );
  const enableAddOnCartItem = useFlag(LaunchDarklyFlag.ENABLE_ADD_ON_AS_CART_ITEM);
  const upsellEnabled = useFlag(LaunchDarklyFlag.ENABLE_CHECKOUT_UPSELL_ITEMS_2);
  const mobileCartUpsellLocation = useFlag(LaunchDarklyFlag.MOBILE_CART_UPSELL_LOCATION);
  const showFooter = useFlag(LaunchDarklyFlag.ENABLE_FOOTER_CART_PAGE);
  const enableTHRewardsTierToggleUI = useFlag(LaunchDarklyFlag.ENABLE_TH_REWARD_TIER_TOGGLE_UI);
  const enableOrdering = useFlag(LaunchDarklyFlag.ENABLE_ORDERING);
  const isMobile = useMediaQuery(MediaQuery.Mobile);

  const orderServiceMode = serverOrder?.cart?.serviceMode;
  const { cartEntriesActiveDayParts } = useCartEntriesDayParts(cartEntries);
  const isCatering = orderServiceMode && isCateringOrder(orderServiceMode);
  const { isInSameRegion } = useUserInSameRegion();
  const offersCtx = useOffersContext();
  const { formatMessage } = useIntl();
  const filteredOfferErrorList = useAppSelector(state =>
    selectors.loyalty.selectIncentiveErrorsFilteredByCode(
      state,
      IncentiveEvaluationErrorCodes.INVALID_PAYMENT_METHOD
    )
  );
  const { redeemableItemsSet } = useRewardDiscount({
    serverOrder,
    cartEntries,
  });

  const cartHasUnavailableOffers = useCartHasUnavailableOffers();
  const { resetStore } = useStoreContext();
  const {
    enableOrderTimedFire,
    enableDeliveryOrderTimedFire,
    enableDriveThruOrderTimedFire,
    isPickupTimeAvailable,
  } = useOrderTimedFire({
    serverOrder,
    cartEntriesActiveDayParts,
  });

  const { isCartTotalNegative = false } = orderCtx?.calculateCartTotalWithDiscount?.() || {};
  const { isBlockedByRegion } = useBlockedByRegion();

  const pricedAndAvailable =
    priceOrderSuccess && !unavailableCartEntries.length && !priceMutationError;

  const legacyOfferIsValid = !offersCtx.selectedOffer || !offersCtx.offerValidationErrors.length;

  const offerIsValid = legacyOfferIsValid && !cartHasUnavailableOffers;

  const [hasInvalidDeliveryOptions, setHasInvalidDeliveryOptions] = useState(false);

  const isPaymentDisabled =
    !!filteredOfferErrorList.length ||
    cartHasUnavailableOffers ||
    isCartTotalNegative ||
    hasInvalidDeliveryOptions;

  useEffect(() => {
    if (!enableOrdering) {
      resetStore();
    }
  }, [enableOrdering, resetStore]);

  return (
    <>
      <ModalBlockRegion show={isBlockedByRegion()} />
      <Helmet title={formatMessage({ id: 'cart' })} />
      <CartPageContainer>
        {isMobile && isInSameRegion && (
          <>
            {enableTHRewardsTierToggleUI && (
              <RewardsPointsContainer>
                <TimsRewardsPoints points={loyaltyDetails?.points ?? 0} />
              </RewardsPointsContainer>
            )}
            <CartRewardDropdown skeletonTheme={SkeletonThemeType.SECONDARY} />
          </>
        )}
        <Content
          isCateringOrder={!!isCatering}
          upsellEnabled={upsellEnabled}
          addOnEnabled={enableAddOnCartItem}
          mobileCartUpsellLocation={mobileCartUpsellLocation}
        >
          <StyledDetails
            enableDeliveryOrderTimedFire={enableDeliveryOrderTimedFire}
            enableDriveThruOrderTimedFire={enableDriveThruOrderTimedFire}
            enableOrderTimedFire={enableOrderTimedFire}
            onTimeSelectorChange={orderCtx.setFireOrderIn}
            onDeliveryStatusChange={setHasInvalidDeliveryOptions}
            timeSelectorValue={orderCtx.fireOrderIn}
            isPickupTimeAvailable={isPickupTimeAvailable}
            serverOrder={serverOrder}
            serviceMode={orderCtx.serviceMode}
            user={user}
            setAreDeliveryDetailsValid={setAreDeliveryDetailsValid}
          />

          {/* rendering in two distinct places for a11y flow */}
          {upsellEnabled && mobileCartUpsellLocation === MobileCartUpsellLocation.AboveYourCart && (
            <StyledUpsell hasBorderAtCheckout />
          )}

          {enableAddOnCartItem && (
            <AddOnContainer>
              <AddOnsItemsContainer />
            </AddOnContainer>
          )}

          <CartWrap>
            <YourCart
              checkoutPaymentMethod={checkoutPaymentMethod}
              redeemableItemsSet={redeemableItemsSet}
              orderStatus={orderStatus}
              serverOrder={serverOrder}
              showAddExtrasBtn={hasAddOns}
              pricedAndAvailable={pricedAndAvailable}
            />
          </CartWrap>

          {/* rendering in two distinct places for a11y flow */}
          {upsellEnabled &&
            mobileCartUpsellLocation === MobileCartUpsellLocation.AbovePaymentDetails && (
              <StyledUpsell hasBorderAtCheckout />
            )}
          <PaymentArea>
            <CartErrorMessages
              isOfferNotAvailable={cartHasUnavailableOffers}
              isCartTotalNegative={isCartTotalNegative}
            />
            <StyledPayment
              disabled={isPaymentDisabled}
              enableOrderTimedFire={enableOrderTimedFire}
              offerIsValid={offerIsValid}
              onPriceOrder={tryPricing}
              order={orderCtx}
              orderStatus={orderStatus}
              pricedAndAvailable={pricedAndAvailable}
              serverOrder={serverOrder}
              areDeliveryDetailsValid={areDeliveryDetailsValid}
            />
          </PaymentArea>
        </Content>
        {showFooter && <PageFooter />}
      </CartPageContainer>
    </>
  );
};

export default React.memo(CartContent);

const PageFooter = styled(Footer)`
  margin-top: auto;
`;
